import * as ft from 'funtypes'
import { objectWith } from '../validation.js'

/**
 * The canonical operator registry.
 *
 * EIPA publishes an operator's legal name ("Tauron Nowe Technologie sp. z o.o.")
 * and a short name, both of which drift. The site tracks brands, so every raw
 * name is folded onto one of the slugs below via {@link resolveOperator}, and
 * anything unrecognised becomes `other`.
 *
 * The registry covers every network with roughly ten or more sites in EIPA -
 * the top fifty, plus a handful of smaller brands a driver would still name.
 * That is 91 % of the country's sites; the long tail behind it is a few hundred
 * hotels, councils and car dealers with one charger each, which belong in
 * `other` rather than in a filter list nobody can scroll.
 *
 * Entries are ordered by how many sites the brand runs, biggest first, because
 * that order is what the filter list and the legend render. It is a snapshot of
 * one day's EIPA export, not a live ranking; brands overtaking each other is not
 * a reason to reorder the file.
 *
 * One brand can hold several EIPA operator codes - a company registers a second
 * one after a merger and never retires the first - so `codes` is a list and
 * GreenWay's three codes count as one network.
 *
 * Colours are drawn from the Okabe-Ito, Paul Tol and ColorBrewer qualitative
 * palettes. With fifty-six brands no palette keeps every pair distinguishable;
 * the ordering above is what makes that survivable, since the hues that stay
 * apart under deuteranopia, protanopia and tritanopia go to the networks a
 * reader actually meets on the map. Brand colour is the outer ring of a dot,
 * never its only encoding - power band drives the fill and the radius.
 */

export const OperatorSlug = ft.Union(
  ft.Literal('greenway'),
  ft.Literal('lidl'),
  ft.Literal('power-dot'),
  ft.Literal('energa'),
  ft.Literal('elocity'),
  ft.Literal('noxo'),
  ft.Literal('eleport'),
  ft.Literal('kaufland'),
  ft.Literal('tauron'),
  ft.Literal('orlen'),
  ft.Literal('budimex-mobility'),
  ft.Literal('charge-polska'),
  ft.Literal('lasy-panstwowe'),
  ft.Literal('evplus'),
  ft.Literal('chargein'),
  ft.Literal('horyzont-ev'),
  ft.Literal('moya'),
  ft.Literal('arinea'),
  ft.Literal('enefit'),
  ft.Literal('qwello'),
  ft.Literal('go-plus-eauto'),
  ft.Literal('polenergia'),
  ft.Literal('eway'),
  ft.Literal('leroy-merlin'),
  ft.Literal('eon'),
  ft.Literal('plugbox'),
  ft.Literal('elomoto'),
  ft.Literal('equay'),
  ft.Literal('beon'),
  ft.Literal('ekoen'),
  ft.Literal('interparking'),
  ft.Literal('ikea'),
  ft.Literal('tesla'),
  ft.Literal('basic-energy'),
  ft.Literal('ionity'),
  ft.Literal('lellek'),
  ft.Literal('shell-recharge'),
  ft.Literal('pure-city'),
  ft.Literal('cichy-zasada'),
  ft.Literal('plichta'),
  ft.Literal('in-balance-grid'),
  ft.Literal('duda-cars'),
  ft.Literal('elbud'),
  ft.Literal('e-powerup'),
  ft.Literal('nexet'),
  ft.Literal('bereadygo'),
  ft.Literal('screencharge'),
  ft.Literal('dpd'),
  ft.Literal('tsl-system'),
  ft.Literal('integracja-b2b'),
  ft.Literal('ecostation'),
  ft.Literal('marmax'),
  ft.Literal('electromobility-one'),
  ft.Literal('allego'),
  ft.Literal('edri-poland'),
  ft.Literal('bp-pulse'),
  ft.Literal('porsche'),
  ft.Literal('other'),
)
export type OperatorSlug = ft.Static<typeof OperatorSlug>

/** The bucket for operators outside the tracked brands. */
export const OTHER_OPERATOR_SLUG = 'other' satisfies OperatorSlug

export const OperatorBrand = objectWith(
  {
    slug: OperatorSlug,
    displayName: ft.String,
    /** `#rrggbb`, lower case. Distinct across the registry. */
    color: ft.String,
    /**
     * Raw EIPA `name` / `short_name` values this brand answers to, matched
     * after {@link normalizeOperatorName}. Also what the site search matches a
     * typed brand against, so an alias is worth adding for any name a driver
     * would use - `moya` for ANWIM, `pkn orlen` for ORLEN.
     */
    aliases: ft.Array(ft.String),
  },
  {
    website: ft.String,
    /** EIPA operator `code` values, matched after {@link normalizeOperatorCode}. */
    codes: ft.Array(ft.String),
  },
)
export type OperatorBrand = ft.Static<typeof OperatorBrand>

export const OPERATORS: readonly OperatorBrand[] = [
  {
    slug: 'greenway',
    displayName: 'GreenWay',
    color: '#009e73',
    website: 'https://greenwaypolska.pl',
    codes: ['PL-GWN', 'PL-GWP', 'PL-7R5'],
    aliases: ['greenway polska', 'greenway', 'gwpl', 'green way'],
  },
  {
    slug: 'lidl',
    displayName: 'Lidl',
    color: '#0072b2',
    website: 'https://www.lidl.pl',
    codes: ['PL-Q55'],
    aliases: ['lidl'],
  },
  {
    slug: 'power-dot',
    displayName: 'Power Dot',
    color: '#d55e00',
    website: 'https://powerdot.pl',
    codes: ['PL-WUA'],
    aliases: ['power dot poland', 'power dot', 'powerdot'],
  },
  {
    slug: 'energa',
    displayName: 'Energa',
    color: '#cc79a7',
    website: 'https://emobility.pl',
    codes: ['PL-KM8', 'PL-E99'],
    aliases: ['energa obrot', 'energa oswietlenie', 'energa'],
  },
  {
    slug: 'elocity',
    displayName: 'Elocity',
    color: '#56b4e9',
    website: 'https://elo.city',
    codes: ['PL-JMG'],
    aliases: ['elocity'],
  },
  {
    slug: 'noxo',
    displayName: 'NOXO Energy',
    color: '#e69f00',
    website: 'https://www.noxo.energy',
    codes: ['PL-NOX'],
    aliases: ['noxo energy', 'noxo'],
  },
  {
    slug: 'eleport',
    displayName: 'Eleport',
    color: '#332288',
    website: 'https://eleport.pl',
    codes: ['PL-A1Y', 'PL-ELP'],
    aliases: ['eleportpl', 'eleport'],
  },
  {
    slug: 'kaufland',
    displayName: 'Kaufland',
    color: '#117733',
    website: 'https://sklep.kaufland.pl',
    codes: ['PL-3T1'],
    aliases: ['kaufland polska markety', 'kaufland'],
  },
  {
    slug: 'tauron',
    displayName: 'Tauron',
    color: '#882255',
    website: 'https://tauron.pl',
    codes: ['PL-BB4'],
    aliases: ['tauron nowe technologie', 'tauron', 'tnt'],
  },
  {
    slug: 'orlen',
    displayName: 'ORLEN',
    color: '#d62728',
    website: 'https://orlen.pl',
    codes: ['PL-PKN'],
    aliases: ['orlen', 'pkn orlen', 'orlen charge'],
  },
  {
    slug: 'budimex-mobility',
    displayName: 'Budimex Mobility',
    color: '#999933',
    website: 'https://budimexmobility.pl',
    codes: ['PL-SSS'],
    aliases: ['budimex mobility', 'budimex'],
  },
  {
    slug: 'charge-polska',
    displayName: 'Charge Polska',
    color: '#aa4499',
    codes: ['PL-8BA'],
    aliases: ['charge polska'],
  },
  {
    slug: 'lasy-panstwowe',
    displayName: 'Lasy Państwowe',
    color: '#66ccee',
    website: 'https://zup.lodz.lasy.gov.pl',
    codes: ['PL-C41'],
    aliases: ['zaklad uslugowo produkcyjny lasow panstwowych', 'lasy panstwowe'],
  },
  {
    slug: 'evplus',
    displayName: 'EV Plus',
    color: '#ee6677',
    website: 'https://evplus.com.pl',
    codes: ['PL-GJC'],
    aliases: ['ev plus', 'evplus'],
  },
  {
    slug: 'chargein',
    displayName: 'ChargeIn',
    color: '#228833',
    website: 'https://www.chargein.com',
    codes: ['PL-FEH'],
    aliases: ['chargein cpo', 'chargein', 'charge in'],
  },
  {
    slug: 'horyzont-ev',
    displayName: 'Horyzont EV',
    color: '#ccbb44',
    website: 'https://www.horyzontev.pl',
    codes: ['PL-1SN'],
    aliases: ['kpe horyzont ev', 'horyzont ev', 'horyzontev'],
  },
  {
    slug: 'moya',
    displayName: 'MOYA',
    color: '#4477aa',
    website: 'https://moyastacja.pl',
    codes: ['PL-ANW'],
    aliases: ['anwim', 'moya'],
  },
  {
    slug: 'arinea',
    displayName: 'Arinea',
    color: '#ee8866',
    website: 'https://arinea.pl',
    codes: ['PL-Z75'],
    // eVolt and EuroCharge both print the EIPA code rather than the brand.
    aliases: ['arinea', 'z75'],
  },
  {
    slug: 'enefit',
    displayName: 'Enefit',
    color: '#bbcc33',
    website: 'https://www.enefit.pl',
    codes: ['PL-T1A'],
    aliases: ['enefit'],
  },
  {
    slug: 'qwello',
    displayName: 'Qwello',
    color: '#77aadd',
    website: 'https://qwello.eu',
    codes: ['PL-QWC', 'DE-QWC'],
    aliases: ['qwello polska', 'qwello', 'gwello'],
  },
  {
    slug: 'go-plus-eauto',
    displayName: 'GO+ EAuto',
    color: '#d98cb3',
    website: 'https://www.gopluseauto.pl',
    codes: ['PL-MYE'],
    aliases: ['go eauto', 'go plus eauto', 'gopluseauto'],
  },
  {
    slug: 'polenergia',
    displayName: 'Polenergia eMobility',
    color: '#1b9e77',
    website: 'https://www.polenergia-emobility.pl',
    codes: ['PL-19X'],
    aliases: ['polenergia emobility', 'polenergia'],
  },
  {
    slug: 'eway',
    displayName: 'Eway',
    color: '#d95f02',
    website: 'https://www.eway.pl',
    codes: ['PL-6XQ'],
    aliases: ['eway', 'e-way24', 'eway24'],
  },
  {
    slug: 'leroy-merlin',
    displayName: 'Leroy Merlin',
    color: '#7570b3',
    website: 'https://leroymerlin.pl',
    codes: ['PL-BYN', 'PL-PZU', 'PL-Z19'],
    aliases: ['leroy merlin inwestycje', 'leroy merlin polska', 'leroy merlin'],
  },
  {
    slug: 'eon',
    displayName: 'E.ON',
    color: '#e7298a',
    website: 'https://eondrive.pl',
    codes: ['PL-EON', 'PL-FPK'],
    aliases: ['e on polska', 'e on', 'eon'],
  },
  {
    slug: 'plugbox',
    displayName: 'Plugbox',
    color: '#66a61e',
    website: 'https://www.plugbox.eu',
    codes: ['PL-JKN'],
    aliases: ['plugbox europe', 'plugbox'],
  },
  {
    slug: 'elomoto',
    displayName: 'Elomoto',
    color: '#e6ab02',
    website: 'https://www.elomoto.eco',
    codes: ['PL-QAB'],
    aliases: ['elomoto'],
  },
  {
    slug: 'equay',
    displayName: 'Equay',
    color: '#a6761d',
    website: 'https://www.equay.pl',
    codes: ['PL-EQU'],
    aliases: ['equay charging network', 'equay'],
  },
  {
    slug: 'beon',
    displayName: 'BeON',
    color: '#386cb0',
    codes: ['PL-2HT'],
    aliases: ['voltpoint', 'beon'],
  },
  {
    slug: 'ekoen',
    displayName: 'EKOEN',
    color: '#bf5b17',
    website: 'https://ekoen.pl',
    codes: ['PL-P2W'],
    aliases: ['ekoen'],
  },
  {
    slug: 'interparking',
    displayName: 'Interparking',
    color: '#6a3d9a',
    website: 'https://www.interparking.pl',
    codes: ['PL-59G'],
    aliases: ['interparking polska', 'interparking'],
  },
  {
    slug: 'ikea',
    displayName: 'IKEA',
    color: '#b15928',
    website: 'https://www.ikea.com/pl',
    codes: ['PL-BUJ'],
    aliases: ['ikea industry poland', 'ikea'],
  },
  {
    slug: 'tesla',
    displayName: 'Tesla',
    color: '#33a02c',
    website: 'https://www.tesla.com/pl_pl',
    codes: ['PL-TSL'],
    aliases: ['tesla poland', 'tesla'],
  },
  {
    slug: 'basic-energy',
    displayName: 'Basic Energy',
    color: '#e06666',
    codes: ['PL-UAC'],
    aliases: ['basic energy'],
  },
  {
    slug: 'ionity',
    displayName: 'IONITY',
    color: '#1f78b4',
    website: 'https://ionity.eu',
    codes: ['PL-IOY'],
    aliases: ['ionity'],
  },
  {
    slug: 'lellek',
    displayName: 'Lellek',
    color: '#7cb342',
    website: 'https://portal.lellek.pl',
    codes: ['PL-KT3', 'PL-RM3', 'PL-ZST', 'PL-5YC', 'PL-6EB', 'PL-HQX'],
    aliases: ['auto lellek group', 'auto lellek', 'lellek'],
  },
  {
    slug: 'shell-recharge',
    displayName: 'Shell Recharge',
    color: '#9a7fbf',
    website: 'https://shellrecharge.com/pl-pl',
    codes: ['NL*TNM'],
    aliases: ['shell polska', 'shell recharge', 'shell'],
  },
  {
    slug: 'pure-city',
    displayName: 'Pure City',
    color: '#e0a33e',
    website: 'https://purecity.pl',
    codes: ['PL-7US'],
    aliases: ['pure city infrastructure', 'pure city', 'purecity'],
  },
  {
    slug: 'cichy-zasada',
    displayName: 'Cichy-Zasada',
    color: '#4fa89b',
    codes: ['PL-35S', 'PL-4WT'],
    aliases: ['grupa cichy zasada', 'cichy zasada'],
  },
  {
    slug: 'plichta',
    displayName: 'Plichta',
    color: '#8e8ac4',
    codes: ['PL-9W5'],
    aliases: ['plichta'],
  },
  {
    slug: 'in-balance-grid',
    displayName: 'In Balance Grid',
    color: '#6fa8c9',
    website: 'https://www.inbalancegrid.pl',
    codes: ['LT*IBG'],
    aliases: ['in balance grid', 'inbalance'],
  },
  {
    slug: 'duda-cars',
    displayName: 'Duda-Cars',
    color: '#a05fa0',
    codes: ['PL-HSU'],
    aliases: ['duda cars'],
  },
  {
    slug: 'elbud',
    displayName: 'ELBUD Bełchatów',
    color: '#8bbf4d',
    website: 'https://www.elbud.com.pl',
    codes: ['PL-X7M'],
    aliases: ['elbud belchatow', 'elbud'],
  },
  {
    slug: 'e-powerup',
    displayName: 'e-PowerUP',
    color: '#e2604f',
    website: 'https://e-powerup.pl',
    codes: ['PL-DPJ'],
    aliases: ['it control', 'e powerup', 'powerup'],
  },
  {
    slug: 'nexet',
    displayName: 'NEXET',
    color: '#5f92b8',
    website: 'https://naladowani.eu',
    codes: ['PL-UPZ'],
    aliases: ['nexet'],
  },
  {
    slug: 'bereadygo',
    displayName: 'BeReady2Go',
    color: '#c9a227',
    website: 'https://bereadygroup.eu',
    codes: ['PL-BRG'],
    aliases: ['brand rock', 'bereadygo', 'be ready 2 go'],
  },
  {
    slug: 'screencharge',
    displayName: 'ScreenCharge',
    color: '#6b9e78',
    website: 'https://www.screencharge.pl',
    codes: ['PL-SCR'],
    aliases: ['screencharge'],
  },
  {
    slug: 'dpd',
    displayName: 'DPD Polska',
    color: '#b5651d',
    website: 'https://www.dpd.com/pl',
    codes: ['PL-DJU'],
    aliases: ['dpd polska', 'dpd'],
  },
  {
    slug: 'tsl-system',
    displayName: 'TSL System',
    color: '#5b8c5a',
    codes: ['PL-J45'],
    aliases: ['tsl system'],
  },
  {
    slug: 'integracja-b2b',
    displayName: 'IntegracjaB2B',
    color: '#ab5b9e',
    website: 'https://ib2b.pl',
    codes: ['PL-PRH'],
    aliases: ['integracjab2b', 'integracja b2b', 'ib2b'],
  },
  {
    slug: 'ecostation',
    displayName: 'EcoStation',
    color: '#3f7d8c',
    website: 'https://ecostation.io',
    codes: ['PL-YN2'],
    aliases: ['ecostation'],
  },
  {
    slug: 'marmax',
    displayName: 'Marmax',
    color: '#c1666b',
    codes: ['PL-P3D'],
    aliases: ['marmax'],
  },
  {
    slug: 'electromobility-one',
    displayName: 'electromobility.ONE',
    color: '#7a6ff0',
    website: 'https://electromobility.one',
    codes: ['PL-6X4'],
    aliases: ['electromobility one', 'electromobility'],
  },
  {
    slug: 'allego',
    displayName: 'Allego',
    color: '#44bb99',
    website: 'https://www.allego.eu',
    codes: ['PL-ALL'],
    aliases: ['allego poland', 'allego'],
  },
  {
    slug: 'edri-poland',
    displayName: 'EDRI Poland',
    color: '#cc6677',
    codes: ['PL-UFC'],
    aliases: ['edri poland', 'edri', 'e on drive infrastructure'],
  },
  {
    slug: 'bp-pulse',
    displayName: 'BP Pulse',
    color: '#2e8b57',
    website: 'https://www.bp.com/pl_pl/poland/home.html',
    codes: ['PL-BPE'],
    aliases: ['bp europa', 'bp pulse', 'bp'],
  },
  {
    slug: 'porsche',
    displayName: 'Porsche',
    color: '#8c564b',
    website: 'https://www.porsche.com/poland',
    // Not an EIPA operator, so no `codes` - see `ge/operatorOverrides.ts`, which
    // assigns this slug by hand to the Porsche-group dealership chargers GE
    // scrapes with no EVSE id to resolve one from.
    aliases: ['porsche centrum', 'audi porsche', 'volkswagen porsche', 'skoda porsche', 'porsche'],
  },
  {
    slug: OTHER_OPERATOR_SLUG,
    displayName: 'Inni operatorzy',
    color: '#7f7f7f',
    aliases: [],
  },
]

/** Every tracked brand, without the `other` bucket. */
export const TRACKED_OPERATORS: readonly OperatorBrand[] = OPERATORS.filter(
  (operator) => operator.slug !== OTHER_OPERATOR_SLUG,
)

export const OPERATORS_BY_SLUG: ReadonlyMap<OperatorSlug, OperatorBrand> = new Map(
  OPERATORS.map((operator) => [operator.slug, operator]),
)

export const operatorBrand = (slug: OperatorSlug): OperatorBrand => {
  const brand = OPERATORS_BY_SLUG.get(slug)
  if (brand === undefined) throw new Error(`Unknown operator slug: ${slug}`)
  return brand
}

/**
 * `OperatorSlug` as a *published artifact* carries it: anything the registry
 * does not know parses as `other` rather than failing the whole file.
 *
 * A published artifact outlives the build that produced it. `stations.geojson`
 * goes out with `stale-while-revalidate=86400`, so for a day after a registry
 * change a returning visitor can be running today's JavaScript against
 * yesterday's artifact - and under a strict union, one retired slug in one
 * feature rejects all nine thousand of them and the map renders nothing.
 *
 * Coercing costs the strictness on the way *in*, which is the cheaper half: the
 * slugs in an artifact are written by {@link resolveOperator} from this same
 * registry, so a value that needs coercing is by construction one this build has
 * retired, and `other` is exactly where the next publish will put it.
 */
export const PublishedOperatorSlug = ft.ParsedValue(ft.String, {
  name: 'OperatorSlug',
  parse: (value) => ({
    success: true,
    value: OPERATORS_BY_SLUG.has(value as OperatorSlug)
      ? (value as OperatorSlug)
      : OTHER_OPERATOR_SLUG,
  }),
  test: OperatorSlug,
})

/**
 * The same lookup for a slug that arrived from outside the type system.
 *
 * A published artifact outlives the build that produced it: a browser holding a
 * cached `stations.geojson` from before a registry change will hand back slugs
 * this build has never heard of. Retiring a brand must not throw a rendering
 * error at whoever loads the page in that window, so an unknown slug reads as
 * `other` - which is what it will be after the next publish anyway.
 */
export const operatorBrandOrOther = (slug: string): OperatorBrand =>
  OPERATORS_BY_SLUG.get(slug as OperatorSlug) ?? operatorBrand(OTHER_OPERATOR_SLUG)

/**
 * Everything a driver might type to mean this brand: the name on screen plus
 * every alias behind it.
 *
 * The site search matches operators through this rather than through
 * `displayName` alone, because the name a network trades under is rarely the
 * one on the sign - ANWIM's chargers say MOYA, and nobody searches for "ANWIM".
 */
export const operatorSearchText = (brand: OperatorBrand): string =>
  [brand.displayName, ...brand.aliases].join(' ')

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

/** Polish letters that Unicode NFD does not decompose into base + mark. */
const NON_DECOMPOSING_DIACRITICS: Readonly<Record<string, string>> = {
  ł: 'l',
  Ł: 'L',
  đ: 'd',
  ø: 'o',
  ß: 'ss',
}

/**
 * Legal-form suffixes, stripped after punctuation has become whitespace so
 * `sp. z o.o.` arrives here as `sp z o o`.
 */
const LEGAL_FORMS: readonly RegExp[] = [
  /\bspolka z ograniczona odpowiedzialnoscia\b/g,
  /\bspolka akcyjna\b/g,
  /\bsp z o o\b/g,
  /\bsp zoo\b/g,
  /\bspzoo\b/g,
  /\bsp k\b/g,
  /\bs k a\b/g,
  /\bp s a\b/g,
  /\bs a\b/g,
  /\boddzial w polsce\b/g,
  /\bgmbh\b/g,
  /\bag\b/g,
  /\bb v\b/g,
  /\bn v\b/g,
  /\bs e\b/g,
  /\bse\b/g,
  /\bltd\b/g,
  /\blimited\b/g,
  /\bllc\b/g,
  /\binc\b/g,
  /\bs r o\b/g,
]

const stripDiacritics = (value: string): string =>
  value
    .replace(/[łŁđøß]/g, (char) => NON_DECOMPOSING_DIACRITICS[char] ?? char)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')

/**
 * Fold a raw operator name into a comparable key: diacritics stripped, lower
 * case, punctuation reduced to single spaces, legal form removed.
 *
 * `"TAURON Nowe Technologie sp. z o.o."` -> `"tauron nowe technologie"`
 */
export const normalizeOperatorName = (value: string): string => {
  const base = stripDiacritics(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

  const withoutLegalForm = LEGAL_FORMS.reduce((acc, pattern) => acc.replace(pattern, ' '), base)
  return withoutLegalForm.replace(/\s+/g, ' ').trim()
}

/** Fold an EIPA operator code into a comparable key: `PL-TNT` -> `PLTNT`. */
export const normalizeOperatorCode = (value: string): string =>
  stripDiacritics(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '')

interface AliasEntry {
  readonly alias: string
  readonly slug: OperatorSlug
}

/**
 * Aliases sorted longest first, so `e on drive infrastructure poland` wins over
 * `e on` when both would match. Ties break on slug for determinism.
 */
const ALIAS_INDEX: readonly AliasEntry[] = OPERATORS.flatMap((operator) =>
  operator.aliases.map((alias) => ({ alias: normalizeOperatorName(alias), slug: operator.slug })),
)
  .filter((entry) => entry.alias !== '')
  .sort((a, b) => b.alias.length - a.alias.length || a.slug.localeCompare(b.slug))

const EXACT_ALIASES: ReadonlyMap<string, OperatorSlug> = new Map(
  ALIAS_INDEX.map((entry) => [entry.alias, entry.slug] as [string, OperatorSlug]),
)

const CODE_INDEX: ReadonlyMap<string, OperatorSlug> = new Map(
  OPERATORS.flatMap((operator) =>
    (operator.codes ?? []).map(
      (code) => [normalizeOperatorCode(code), operator.slug] as [string, OperatorSlug],
    ),
  ),
)

/**
 * Match a normalized name against the alias index: exact hit first, then the
 * longest alias that the name starts with on a word boundary. The boundary
 * matters - it stops `ekoen` (EKOEN) from swallowing `ekoenergetyka`, and `bp`
 * (BP Pulse) from swallowing `bpk nieruchomosci`.
 */
const matchAlias = (normalized: string): OperatorSlug | undefined => {
  if (normalized === '') return undefined

  const exact = EXACT_ALIASES.get(normalized)
  if (exact !== undefined) return exact

  return ALIAS_INDEX.find((entry) => normalized.startsWith(`${entry.alias} `))?.slug
}

/**
 * Resolve a raw EIPA operator onto a canonical slug.
 *
 * Tried in order: registry code, full name, short name. Anything unmatched
 * returns `other`, never throws.
 */
export const resolveOperator = (
  name?: string | null,
  shortName?: string | null,
  code?: string | null,
): OperatorSlug => {
  if (code != null && code !== '') {
    const byCode = CODE_INDEX.get(normalizeOperatorCode(code))
    if (byCode !== undefined) return byCode
  }

  const byName = matchAlias(normalizeOperatorName(name ?? ''))
  if (byName !== undefined) return byName

  const byShortName = matchAlias(normalizeOperatorName(shortName ?? ''))
  if (byShortName !== undefined) return byShortName

  return OTHER_OPERATOR_SLUG
}
